/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WASP Dashboard v1.0.2 - Sección de Gestión de Grupos
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file GroupsManagementSection.tsx
 * @description Gestión completa de grupos de WhatsApp
 * @version 1.0.2
 * @date 2024-11-02
 * @architecture Frontend → Proxy → WASP
 * 
 * FUNCIONALIDADES:
 * ├─ Ver grupos actuales (lista con metadata)
 * ├─ Unirse a grupo por enlace de invitación
 * ├─ Ver invitaciones pendientes
 * ├─ Aceptar/rechazar invitaciones
 * └─ Abandonar grupos
 * 
 * ENDPOINTS USADOS:
 * - GET  /api/whatsapp/wasp/groups-status
 * - POST /api/whatsapp/wasp/join-group
 * - GET  /api/whatsapp/wasp/pending-invitations
 * - POST /api/whatsapp/wasp/process-invitation
 * - POST /api/whatsapp/wasp/leave-group
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect } from 'react';
import { Card, Button, ListGroup, Badge, Form, InputGroup, Spinner, Alert, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faUserPlus, 
  faRightFromBracket, 
  faEnvelope,
  faCheck,
  faTimes,
  faLink
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { getHatateToken } from '../../helpers/utils'; // ← FIX: Importar helper correcto
import { head } from 'lodash';

// ═══════════════════════════════════════════════════════════════════════════
// INTERFACES
// ═══════════════════════════════════════════════════════════════════════════

interface Group {
  id: string;
  name: string;
  participants: number;
  isAdmin: boolean;
  description?: string;
}

interface Invitation {
  groupId: string;
  groupName: string;
  inviter: string;
  timestamp: string;
}

interface GroupsManagementSectionProps {
  user: any;
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE
// ═══════════════════════════════════════════════════════════════════════════

const GroupsManagementSection: React.FC<GroupsManagementSectionProps> = ({ user }) => {
  
  // Estados
  const [groups, setGroups] = useState<Group[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [joiningGroup, setJoiningGroup] = useState(false);
  const [showInvitationsModal, setShowInvitationsModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const getHeaders = () => ({
    Authorization: getHatateToken()
  });

  const apiUrl = 'https://backend.anubisai.net/api/';

  // ═══════════════════════════════════════════════════════════════════════
  // FETCH GRUPOS
  // ═══════════════════════════════════════════════════════════════════════

  const fetchGroups = async () => {
    setLoading(true);
    setError(null);

     const requestBody = {
        headers: getHeaders(),
        mlStuff: {
            whatsAppServerPort: user.mlStuff?.whatsAppServerPort,
            whatsAppApiPort: user.mlStuff?.whatsAppApiPort,
            mlServerPort: user.mlStuff?.mlServerPort
        }
    };
    
    try {
      const response = await axios.get(
        `${apiUrl}whatsapp/wasp/groups-status`,
        requestBody
      );

      if (response.data.success) {
        setGroups(response.data.groups || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar grupos');
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // FETCH INVITACIONES
  // ═══════════════════════════════════════════════════════════════════════

  const fetchInvitations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${apiUrl}whatsapp/wasp/pending-invitations`, 
        { headers: getHeaders(), mlStuff: {
            whatsAppServerPort: user.mlStuff?.whatsAppServerPort,
            whatsAppApiPort: user.mlStuff?.whatsAppApiPort,
            mlServerPort: user.mlStuff?.mlServerPort
        } }
      );

      if (response.data.success) {
        setInvitations(response.data.invitations || []);
      }
    } catch (err) {
      console.error('Error fetching invitations:', err);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // UNIRSE A GRUPO POR ENLACE
  // ═══════════════════════════════════════════════════════════════════════

  const handleJoinGroup = async () => {
    if (!inviteLink.trim()) {
      setError('Por favor ingresa un enlace de invitación');
      return;
    }

    setJoiningGroup(true);
    setError(null);
    setSuccess(null);

    const requestBody = {
        inviteCode: inviteLink,
        mlStuff: {
            whatsAppServerPort: user.mlStuff?.whatsAppServerPort,
            whatsAppApiPort: user.mlStuff?.whatsAppApiPort,
            mlServerPort: user.mlStuff?.mlServerPort
        }
    };
    
    const requestConfig = {
        headers: getHeaders(), 
    };

    try {
      const response = await axios.post(
        `${apiUrl}whatsapp/wasp/join-group`, 
        requestBody,
        requestConfig
      );

      if (response.data.success) {
        setSuccess('Te has unido al grupo exitosamente');
        setInviteLink('');
        fetchGroups();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al unirse al grupo');
    } finally {
      setJoiningGroup(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // PROCESAR INVITACIÓN
  // ═══════════════════════════════════════════════════════════════════════

  const handleProcessInvitation = async (groupId: string, accept: boolean) => {
    try {
      const response = await axios.get(
        `${apiUrl}whatsapp/wasp/process-invitation`, 
        { headers: getHeaders(), mlStuff: {
            whatsAppServerPort: user.mlStuff?.whatsAppServerPort,
            whatsAppApiPort: user.mlStuff?.whatsAppApiPort,
            mlServerPort: user.mlStuff?.mlServerPort
        } }
      );

      setSuccess(accept ? 'Invitación aceptada' : 'Invitación rechazada');
      fetchInvitations();
      if (accept) fetchGroups();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al procesar invitación');
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // ABANDONAR GRUPO
  // ═══════════════════════════════════════════════════════════════════════

  const handleLeaveGroup = async (groupId: string, groupName: string) => {
    if (!window.confirm(`¿Seguro que deseas abandonar "${groupName}"?`)) return;

    try {
      const response = await axios.get(
        `${apiUrl}whatsapp/wasp/leave-group`, 
        { headers: getHeaders(), mlStuff: {
            whatsAppServerPort: user.mlStuff?.whatsAppServerPort,
            whatsAppApiPort: user.mlStuff?.whatsAppApiPort,
            mlServerPort: user.mlStuff?.mlServerPort
        } }
      );

      setSuccess('Has abandonado el grupo');
      fetchGroups();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al abandonar grupo');
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // EFECTOS
  // ═══════════════════════════════════════════════════════════════════════

  useEffect(() => {
    fetchGroups();
    fetchInvitations();
  }, []);

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════

  return (
    <>
      <Card style={{ 
        border: 'none', 
        borderRadius: '12px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
        backgroundColor: 'var(--bs-body-bg)',
        marginBottom: '24px'
      }}>
        <Card.Header style={{ 
          backgroundColor: 'var(--bs-body-bg)', 
          borderBottom: '1px solid var(--bs-border-color)', 
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div className="d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faUsers} style={{ color: '#10b981' }} />
            <h5 className="mb-0" style={{ fontWeight: 600 }}>Gestión de Grupos</h5>
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowInvitationsModal(true)}
          >
            <FontAwesomeIcon icon={faEnvelope} className="me-2" />
            Invitaciones {invitations.length > 0 && (
              <Badge bg="danger" className="ms-1">{invitations.length}</Badge>
            )}
          </Button>
        </Card.Header>

        <Card.Body style={{ padding: '24px' }}>
          
          {/* Alertas */}
          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

          {/* Unirse por enlace */}
          <div className="mb-4">
            <h6 style={{ fontWeight: 600, marginBottom: '12px' }}>Unirse a un Grupo</h6>
            <InputGroup>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faLink} />
              </InputGroup.Text>
              <Form.Control
                placeholder="https://chat.whatsapp.com/..."
                value={inviteLink}
                onChange={(e) => setInviteLink(e.target.value)}
                disabled={joiningGroup}
              />
              <Button 
                variant="success" 
                onClick={handleJoinGroup}
                disabled={joiningGroup || !inviteLink.trim()}
              >
                {joiningGroup ? (
                  <><Spinner size="sm" className="me-2" />Uniéndose...</>
                ) : (
                  <><FontAwesomeIcon icon={faUserPlus} className="me-2" />Unirse</>
                )}
              </Button>
            </InputGroup>
          </div>

          {/* Lista de grupos */}
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 style={{ fontWeight: 600, marginBottom: 0 }}>Grupos Actuales</h6>
              <Button variant="link" size="sm" onClick={fetchGroups} disabled={loading}>
                {loading ? <Spinner size="sm" /> : 'Actualizar'}
              </Button>
            </div>

            {loading && groups.length === 0 ? (
              <div className="text-center py-4">
                <Spinner />
              </div>
            ) : groups.length === 0 ? (
              <div className="text-center py-4 text-muted">
                <FontAwesomeIcon icon={faUsers} size="3x" className="mb-3" style={{ opacity: 0.3 }} />
                <p>No estás en ningún grupo</p>
              </div>
            ) : (
              <ListGroup>
                {groups.map((group) => (
                  <ListGroup.Item 
                    key={group.id}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <div style={{ flex: 1 }}>
                      <div className="d-flex align-items-center gap-2">
                        <strong>{group.name}</strong>
                        {group.isAdmin && (
                          <Badge bg="primary" pill>Admin</Badge>
                        )}
                      </div>
                      <small className="text-muted">
                        {group.participants} participantes
                        {group.description && ` • ${group.description}`}
                      </small>
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleLeaveGroup(group.id, group.name)}
                    >
                      <FontAwesomeIcon icon={faRightFromBracket} className="me-1" />
                      Salir
                    </Button>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Modal de Invitaciones */}
      <Modal show={showInvitationsModal} onHide={() => setShowInvitationsModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Invitaciones Pendientes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {invitations.length === 0 ? (
            <p className="text-center text-muted">No hay invitaciones pendientes</p>
          ) : (
            <ListGroup>
              {invitations.map((inv) => (
                <ListGroup.Item key={inv.groupId}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{inv.groupName}</strong>
                      <br />
                      <small className="text-muted">
                        Invitado por {inv.inviter}
                      </small>
                    </div>
                    <div className="d-flex gap-2">
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleProcessInvitation(inv.groupId, true)}
                      >
                        <FontAwesomeIcon icon={faCheck} />
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleProcessInvitation(inv.groupId, false)}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </Button>
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default GroupsManagementSection;